import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Heart, Gift, Building, Users, Check } from 'lucide-react';
import { toast } from 'sonner';
import { PayPalWrapper as PayPalCheckout } from '../components/PayPalWrapper';
import SEOHead from '../components/SEOHead';

const DONATION_AMOUNTS = [25, 50, 100, 250, 500, 1000];

export const Support = () => {
  const [donationAmount, setDonationAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [showPayPal, setShowPayPal] = useState(false);
  const [donorInfo, setDonorInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: 'TX',
    zip: '',
    isAnonymous: false,
    dedicationType: 'none',
    dedicationName: '',
    message: ''
  });

  const handleAmountSelect = (amount) => {
    setDonationAmount(amount);
    setCustomAmount('');
    setShowPayPal(false);
  };

  const handleCustomAmount = (value) => {
    setCustomAmount(value);
    setDonationAmount('custom');
    setShowPayPal(false);
  };

  const getFinalAmount = () => {
    if (donationAmount === 'custom') {
      return parseFloat(customAmount) || 0;
    }
    return parseFloat(donationAmount) || 0;
  };

  const handleProceedToPayment = () => {
    const amount = getFinalAmount();
    if (amount < 1) {
      toast.error('Please select or enter a donation amount');
      return;
    }
    if (!donorInfo.firstName || !donorInfo.lastName || !donorInfo.email) {
      toast.error('Please fill in required fields (First Name, Last Name, Email)');
      return;
    }
    setShowPayPal(true);
  };

  const getDonationItems = () => {
    const amount = getFinalAmount();
    let description = 'Donation to Coppini Academy of Fine Art';
    if (donorInfo.dedicationType === 'honor') {
      description += ` - In Honor of ${donorInfo.dedicationName}`;
    } else if (donorInfo.dedicationType === 'memory') {
      description += ` - In Memory of ${donorInfo.dedicationName}`;
    }
    
    return [{
      name: 'Donation to CAFA',
      description: description,
      quantity: 1,
      price: amount,
      category: 'donation'
    }];
  };

  const handlePaymentSuccess = (result) => {
    toast.success('Thank you for your generous donation!', {
      description: 'Your contribution helps preserve classical art education.'
    });
    setShowPayPal(false);
    setDonationAmount('');
    setCustomAmount('');
    setDonorInfo({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: 'TX',
      zip: '',
      isAnonymous: false,
      dedicationType: 'none',
      dedicationName: '',
      message: ''
    });
  };

  const handlePaymentError = (error) => {
    toast.error('Payment failed. Please try again.');
    console.error('Payment error:', error);
  };

  const handleDonate = () => {
    const amount = getFinalAmount();
    if (amount < 1) {
      toast.error('Please select or enter a donation amount');
      return;
    }
    if (!donorInfo.firstName || !donorInfo.lastName || !donorInfo.email) {
      toast.error('Please fill in required fields');
      return;
    }
    
    // For now, redirect to PayPal or show success message
    toast.success(`Thank you for your generous donation of $${amount}! You will be redirected to complete payment.`);
    
    // In production, this would redirect to PayPal or a payment processor
    // window.location.href = `https://www.paypal.com/donate?...`;
  };

  return (
    <div className="min-h-screen bg-white" data-testid="support-page">
      <SEOHead
        title="Support CAFA — Donate"
        description="Support The Coppini Academy of Fine Art with a tax-deductible donation. Help preserve classical and representational art education in San Antonio."
        canonicalPath="/support"
      />
      {/* Hero */}
      <section className="bg-stone-900 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_sanantonioart/artifacts/a096xmvl_logobanner_poster_white.png"
            alt="The Coppini Academy of Fine Art — est. 1945"
            className="max-w-full h-auto mx-auto mb-8"
          />
          <Heart className="w-16 h-16 text-cafa-red mx-auto mb-6" />
          <h1 className="text-4xl sm:text-5xl font-serif text-white mb-6">Support CAFA</h1>
          <p className="text-xl text-stone-300">
            Help us preserve and promote classical representational art for future generations
          </p>
        </div>
      </section>

      {/* Why Support */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-stone-900 mb-6 text-center">Why Your Support Matters</h2>
          <p className="text-lg text-stone-700 leading-relaxed mb-8 text-center">
            The Coppini Academy of Fine Art is a 501(c)(3) not-for-profit organization dedicated to the promotion 
            and preservation of representational art. Your tax-deductible donation helps us maintain our historic 
            facility, support our educational programs, and continue the legacy of founder Pompeo Coppini.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            <div className="text-center p-6 bg-stone-50 rounded-lg">
              <Building className="w-12 h-12 text-cafa-red mx-auto mb-4" />
              <h3 className="font-semibold text-stone-900 mb-2">Preserve History</h3>
              <p className="text-sm text-stone-600">Help maintain our historic 1936 studio built by Pompeo Coppini</p>
            </div>
            <div className="text-center p-6 bg-stone-50 rounded-lg">
              <Users className="w-12 h-12 text-cafa-red mx-auto mb-4" />
              <h3 className="font-semibold text-stone-900 mb-2">Support Artists</h3>
              <p className="text-sm text-stone-600">Fund scholarships and programs for aspiring artists</p>
            </div>
            <div className="text-center p-6 bg-stone-50 rounded-lg">
              <Gift className="w-12 h-12 text-cafa-red mx-auto mb-4" />
              <h3 className="font-semibold text-stone-900 mb-2">Grow Programs</h3>
              <p className="text-sm text-stone-600">Expand workshops, classes, and community outreach</p>
            </div>
          </div>
        </div>
      </section>

      {/* Studio Photo */}
      <section className="py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_fine-art-academy-1/artifacts/h567bdbs_10_1440.jpg"
            alt="Artists at work in the Coppini Academy studio"
            className="w-full rounded-lg shadow-lg"
          />
        </div>
      </section>

      {/* Donation Checkout */}
      <section className="py-16 bg-stone-50" id="donate">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-stone-200 shadow-lg">
            <CardContent className="p-8">
              <h2 className="text-2xl font-serif text-stone-900 mb-6 text-center">Make a Donation</h2>
              <p className="text-stone-600 text-center mb-8">
                All donations are tax-deductible to the full extent allowed by law.
              </p>

              {/* Donation Amount Selection */}
              <div className="mb-8">
                <Label className="text-lg font-medium text-stone-900 mb-4 block">Select Donation Amount</Label>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {DONATION_AMOUNTS.map((amount) => (
                    <Button
                      key={amount}
                      type="button"
                      variant={donationAmount === amount ? 'default' : 'outline'}
                      className={donationAmount === amount 
                        ? 'bg-cafa-red hover:bg-cafa-red-900 text-white' 
                        : 'border-stone-300 hover:border-cafa-red hover:text-cafa-red'}
                      onClick={() => handleAmountSelect(amount)}
                      data-testid={`donation-amount-${amount}`}
                    >
                      ${amount}
                    </Button>
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant={donationAmount === 'custom' ? 'default' : 'outline'}
                    className={donationAmount === 'custom' 
                      ? 'bg-cafa-red hover:bg-cafa-red-900 text-white' 
                      : 'border-stone-300 hover:border-cafa-red hover:text-cafa-red'}
                    onClick={() => setDonationAmount('custom')}
                  >
                    Other
                  </Button>
                  {donationAmount === 'custom' && (
                    <div className="flex items-center flex-1">
                      <span className="text-stone-500 mr-2">$</span>
                      <Input
                        type="number"
                        placeholder="Enter amount"
                        value={customAmount}
                        onChange={(e) => handleCustomAmount(e.target.value)}
                        className="flex-1"
                        min="1"
                        data-testid="custom-amount-input"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Donor Information */}
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-stone-900">Donor Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input
                      id="firstName"
                      value={donorInfo.firstName}
                      onChange={(e) => setDonorInfo({...donorInfo, firstName: e.target.value})}
                      required
                      data-testid="donor-firstname"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input
                      id="lastName"
                      value={donorInfo.lastName}
                      onChange={(e) => setDonorInfo({...donorInfo, lastName: e.target.value})}
                      required
                      data-testid="donor-lastname"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={donorInfo.email}
                      onChange={(e) => setDonorInfo({...donorInfo, email: e.target.value})}
                      required
                      data-testid="donor-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={donorInfo.phone}
                      onChange={(e) => setDonorInfo({...donorInfo, phone: e.target.value})}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    value={donorInfo.address}
                    onChange={(e) => setDonorInfo({...donorInfo, address: e.target.value})}
                  />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      value={donorInfo.city}
                      onChange={(e) => setDonorInfo({...donorInfo, city: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State</Label>
                    <Input
                      id="state"
                      value={donorInfo.state}
                      onChange={(e) => setDonorInfo({...donorInfo, state: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="zip">ZIP Code</Label>
                    <Input
                      id="zip"
                      value={donorInfo.zip}
                      onChange={(e) => setDonorInfo({...donorInfo, zip: e.target.value})}
                    />
                  </div>
                </div>

                {/* Dedication Options */}
                <div className="space-y-4 pt-4 border-t border-stone-200">
                  <h3 className="text-lg font-medium text-stone-900">Dedication (Optional)</h3>
                  <div className="space-y-2">
                    <Label>Make this gift</Label>
                    <Select 
                      value={donorInfo.dedicationType} 
                      onValueChange={(v) => setDonorInfo({...donorInfo, dedicationType: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No dedication</SelectItem>
                        <SelectItem value="honor">In Honor of</SelectItem>
                        <SelectItem value="memory">In Memory of</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {donorInfo.dedicationType !== 'none' && (
                    <div className="space-y-2">
                      <Label htmlFor="dedicationName">
                        {donorInfo.dedicationType === 'honor' ? 'Honoree Name' : 'Memorial Name'}
                      </Label>
                      <Input
                        id="dedicationName"
                        value={donorInfo.dedicationName}
                        onChange={(e) => setDonorInfo({...donorInfo, dedicationName: e.target.value})}
                        placeholder={donorInfo.dedicationType === 'honor' ? 'Person being honored' : 'Person being remembered'}
                      />
                    </div>
                  )}
                </div>

                {/* Anonymous Option */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="anonymous"
                    checked={donorInfo.isAnonymous}
                    onChange={(e) => setDonorInfo({...donorInfo, isAnonymous: e.target.checked})}
                    className="rounded border-stone-300"
                  />
                  <Label htmlFor="anonymous" className="text-sm font-normal">
                    Make this donation anonymous
                  </Label>
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <Label htmlFor="message">Message (Optional)</Label>
                  <textarea
                    id="message"
                    className="w-full min-h-[80px] px-3 py-2 border border-stone-300 rounded-md text-sm"
                    value={donorInfo.message}
                    onChange={(e) => setDonorInfo({...donorInfo, message: e.target.value})}
                    placeholder="Add a personal message with your donation..."
                  />
                </div>

                {/* PayPal Checkout or Proceed Button */}
                <div className="pt-6">
                  {showPayPal ? (
                    <div className="space-y-4">
                      <PayPalCheckout
                        items={getDonationItems()}
                        payerEmail={donorInfo.email}
                        payerName={`${donorInfo.firstName} ${donorInfo.lastName}`}
                        onSuccess={handlePaymentSuccess}
                        onError={handlePaymentError}
                        onCancel={() => setShowPayPal(false)}
                        registrationData={{
                          type: 'donation',
                          donor_info: donorInfo,
                          amount: getFinalAmount()
                        }}
                      />
                      <Button 
                        variant="outline"
                        onClick={() => setShowPayPal(false)}
                        className="w-full"
                      >
                        Back to Edit
                      </Button>
                    </div>
                  ) : (
                    <Button 
                      onClick={handleProceedToPayment}
                      className="w-full bg-cafa-red hover:bg-cafa-red-900 text-white text-lg py-6"
                      disabled={getFinalAmount() < 1}
                      data-testid="donate-submit-btn"
                    >
                      <Heart className="mr-2" size={20} />
                      Donate {getFinalAmount() > 0 ? `$${getFinalAmount()}` : ''}
                    </Button>
                  )}
                  <p className="text-xs text-stone-500 text-center mt-4">
                    Your donation is tax-deductible. EIN: 23-7245413
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Other Ways to Give */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-stone-900 mb-8 text-center">Other Ways to Give</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border-stone-200">
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold text-stone-900 mb-3">Become a Member</h3>
                <p className="text-stone-600 mb-4">
                  Join our community of artists and supporters with an annual or lifetime membership.
                </p>
                <Button asChild variant="outline" className="border-cafa-red text-cafa-red hover:bg-cafa-red-50">
                  <a href="/membership">View Membership Options</a>
                </Button>
              </CardContent>
            </Card>
            
            <Card className="border-stone-200">
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold text-stone-900 mb-3">Planned Giving</h3>
                <p className="text-stone-600 mb-4">
                  Include CAFA in your estate planning to leave a lasting legacy for classical art education.
                </p>
                <Button asChild variant="outline" className="border-cafa-red text-cafa-red hover:bg-cafa-red-50">
                  <a href="/contact">Contact Us</a>
                </Button>
              </CardContent>
            </Card>
            
            <Card className="border-stone-200">
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold text-stone-900 mb-3">Corporate Sponsorship</h3>
                <p className="text-stone-600 mb-4">
                  Partner with us to sponsor workshops, exhibitions, or programs.
                </p>
                <Button asChild variant="outline" className="border-cafa-red text-cafa-red hover:bg-cafa-red-50">
                  <a href="/contact">Learn More</a>
                </Button>
              </CardContent>
            </Card>
            
            <Card className="border-stone-200">
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold text-stone-900 mb-3">Volunteer</h3>
                <p className="text-stone-600 mb-4">
                  Donate your time and skills to help with events, exhibitions, and programs.
                </p>
                <Button asChild variant="outline" className="border-cafa-red text-cafa-red hover:bg-cafa-red-50">
                  <a href="/contact">Get Involved</a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact for Questions */}
      <section className="py-16 bg-stone-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-serif mb-4">Questions About Giving?</h2>
          <p className="text-stone-300 mb-8">
            Contact us to learn more about how your support makes a difference.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild className="bg-cafa-red hover:bg-cafa-red-700 text-white">
              <a href="mailto:info@thecoppini.org">Email Us</a>
            </Button>
            <Button asChild variant="outline" className="border-white text-white hover:bg-white hover:text-stone-900">
              <a href="tel:210-530-8445">Call (210) 530-8445</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
